// frontend/src/pages/Pricing.jsx
// ✅ FIXED: Removed all emojis + Fixed desktop guide/try tab visibility
import React from "react";
import Screen from "../uizard/ui/Screen";
import { Link, useNavigate } from "react-router-dom";
import { ACTIVE_STYLES } from "../shared/styles";
import { toast } from "../lib/api";

export default function StylesShowcase() {
  const navigate = useNavigate();
  const [selected, setSelected] = React.useState(ACTIVE_STYLES[0]?.key || "");
  const [search, setSearch] = React.useState("");
  const [activeTab, setActiveTab] = React.useState("gallery"); // gallery, guide, try

  const filteredStyles = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ACTIVE_STYLES;
    return ACTIVE_STYLES.filter((s) => {
      const hay = [
        s.key,
        s.name,
        s.hint,
        Array.isArray(s.tags) ? s.tags.join(" ") : "",
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [search]);

  React.useEffect(() => {
    if (!filteredStyles.some((s) => s.key === selected)) {
      setSelected(filteredStyles[0]?.key || "");
    }
  }, [filteredStyles, selected]);

  // Reset to gallery tab when style changes
  React.useEffect(() => {
    setActiveTab("gallery");
  }, [selected]);

  const active =
    React.useMemo(
      () => ACTIVE_STYLES.find((x) => x.key === selected) || ACTIVE_STYLES[0],
      [selected]
    ) || null;

  const imgs = React.useMemo(() => {
    if (!active) return [];
    const ex = Array.isArray(active.examples) ? active.examples.filter(Boolean) : [];
    if (ex.length) return ex;
    return [active.img || `/assets/styles/${active.key}.jpg`];
  }, [active]);

  const [heroIx, setHeroIx] = React.useState(0);
  React.useEffect(() => setHeroIx(0), [selected]);

  const hasMany = imgs.length > 1;
  const hero = imgs[heroIx] || imgs[0] || "";

  const step = React.useCallback(
    (dir) => {
      if (!hasMany) return;
      setHeroIx((i) => (i + dir + imgs.length) % imgs.length);
    },
    [hasMany, imgs.length]
  );

  React.useEffect(() => {
    function onKey(e) {
      if (!hasMany) return;
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "ArrowRight") step(+1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hasMany, step]);

  const starters =
    (active?.starters && active.starters.length
      ? active.starters
      : [
          `${active?.key}, pumpkin critter, soft rim light`,
          `${active?.key}, toy ghost, moody glow`,
          `${active?.key}, swampy forest, cinematic shadows`,
        ]).filter(Boolean);

  const trySearch = (prompt) => 
    `?style=${encodeURIComponent(active?.key || "")}` +
    `&prompt=${encodeURIComponent(prompt || starters[0] || "")}` +
    `&suggest=${encodeURIComponent(JSON.stringify(starters))}`;

  const copyPrompt = (prompt) => {
    navigator.clipboard.writeText(prompt)
      .then(() => toast("Prompt copied!"))
      .catch(() => toast("Couldn't copy"));
  };

  const tryPrompt = (prompt) => {
    navigate({ pathname: "/create", search: trySearch(prompt) });
  };

  return (
    <Screen>
      <StyleTag />

      <header className="styles-hero">
        <h1
          className="h1"
          style={{
            background:
              "var(--ab-hero-grad, linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%))",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          Create with Styles
        </h1>
        <p className="lead">
          Visual examples, how to prompt, and try it.
        </p>
      </header>

      {/* Toolbar */}
      <section className="styles-toolbar" aria-label="Style picker">
        <div className="search">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search styles…"
            aria-label="Search styles"
          />
          {search && (
            <button className="clear" onClick={() => setSearch("")} aria-label="Clear search">
              ✕
            </button>
          )}
        </div>

        <div className="style-dropdown-wrapper">
          <label htmlFor="style-select" className="dropdown-label">Style Id</label>
          <select
            id="style-select"
            className="style-dropdown"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            {filteredStyles.map((s) => (
              <option key={s.key} value={s.key}>
                {s.name} {s.status === "in-dev" ? "[In Dev]" : ""}
              </option>
            ))}
          </select>
        </div>

        {filteredStyles.length === 0 && (
          <div className="no-results">No styles match "{search}".</div>
        )}
      </section>

      {/* Style info card */}
      {active && (
        <section className="floating-wrap">
          <div className="floating-card">
            {/* Gallery Section (left on desktop, top on mobile) */}
            <div className="floating-media-wrapper">
              {/* Mobile header with badge */}
              <div className="style-header-mobile">
                <div className="style-name-row">
                  <h2 className="style-name">{active.name}</h2>
                  {active.status === "in-dev" && (
                    <span className="dev-badge" title="This style is still being refined">
                      In Dev
                    </span>
                  )}
                </div>
                <p className="style-hint">{active.hint}</p>
              </div>

              {/* Tabs - Mobile Only */}
              <div className="tabs-mobile">
                <button
                  className={`tab ${activeTab === "gallery" ? "active" : ""}`}
                  onClick={() => setActiveTab("gallery")}
                >
                  Gallery
                </button>
                <button
                  className={`tab ${activeTab === "guide" ? "active" : ""}`}
                  onClick={() => setActiveTab("guide")}
                >
                  Guide
                </button>
                <button
                  className={`tab ${activeTab === "try" ? "active" : ""}`}
                  onClick={() => setActiveTab("try")}
                >
                  Try It
                </button>
              </div>

              {/* Gallery Tab Content */}
              {activeTab === "gallery" && (
                <div className="floating-media">
                  {hero ? (
                    <img
                      src={hero}
                      alt={`${active.name} example ${heroIx + 1} of ${imgs.length}`}
                      loading="eager"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  ) : (
                    <div className="floating-fallback" />
                  )}

                  {hasMany && (
                    <>
                      <button
                        className="nav-arrow left"
                        onClick={() => step(-1)}
                        aria-label="Previous example"
                        title="Previous"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path d="M15 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      <button
                        className="nav-arrow right"
                        onClick={() => step(+1)}
                        aria-label="Next example"
                        title="Next"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </>
                  )}

                  {hasMany && (
                    <div className="dots">
                      {imgs.map((_, i) => (
                        <button
                          key={i}
                          className={`dot ${i === heroIx ? "on" : ""}`}
                          onClick={() => setHeroIx(i)}
                          aria-label={`Show example ${i + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Guide Tab Content - Mobile Only */}
              {activeTab === "guide" && (
                <div className="tab-content-mobile">
                  <div className="content-section">
                    <h3 className="section-title">What to expect</h3>
                    <ul className="section-list">
                      <li>Candy-lit mushroom forests <b>{active.name}</b></li>
                      <li>Bold palettes</li>
                      <li>Cozy fantasy creatures</li>
                      <li>Soft glow and dreamlike lighting</li>
                    </ul>
                  </div>

                  <div className="content-section">
                    <h3 className="section-title">Prompt starters</h3>
                    <div className="prompts-list">
                      {starters.map((p, i) => (
                        <div key={i} className="prompt-item">
                          <code className="prompt-text">{p}</code>
                          <div className="prompt-actions">
                            <button 
                              className="btn-icon" 
                              onClick={() => copyPrompt(p)}
                              title="Copy prompt"
                            >
                              Copy
                            </button>
                            <button 
                              className="btn-try-mini" 
                              onClick={() => tryPrompt(p)}
                            >
                              Try →
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="content-section">
                    <h3 className="section-title">Use cases</h3>
                    <ul className="section-list">
                      <li>Story worlds & worldbuilding</li>
                      <li>Whimsical characters & scenes</li>
                      <li>Social visuals with bold, candy-glow vibes</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Try Tab Content - Mobile Only */}
              {activeTab === "try" && (
                <div className="tab-content-mobile">
                  <h3 className="section-title">Quick start with {active.name}</h3>
                  <p className="section-hint">
                    Tap any prompt to generate instantly:
                  </p>
                  
                  <div className="quick-prompts">
                    {starters.map((p, i) => (
                      <button
                        key={i}
                        className="quick-prompt-btn"
                        onClick={() => tryPrompt(p)}
                      >
                        <span className="prompt-icon">→</span>
                        <span className="prompt-label">{p}</span>
                        <span className="prompt-arrow">→</span>
                      </button>
                    ))}
                  </div>

                  <div className="or-divider">
                    <span>or write your own</span>
                  </div>

                  <Link 
                    className="btn btn-custom" 
                    to={{ pathname: "/create", search: trySearch("") }}
                  >
                    Custom Prompt
                  </Link>
                </div>
              )}
            </div>

            {/* Info Section (right on desktop, hidden on mobile in tab mode) */}
            <div className="floating-info">
              {/* Desktop header with badge */}
              <div className="style-header-desktop">
                <div className="style-name-row">
                  <h2 className="style-name">{active.name}</h2>
                  {active.status === "in-dev" && (
                    <span className="dev-badge" title="This style is still being refined">
                      In Dev
                    </span>
                  )}
                </div>
                <p className="style-hint">{active.hint}</p>
              </div>

              {/* Tabs - Desktop Only */}
              <div className="tabs-desktop">
                <button
                  className={`tab ${activeTab === "gallery" ? "active" : ""}`}
                  onClick={() => setActiveTab("gallery")}
                >
                  Gallery
                </button>
                <button
                  className={`tab ${activeTab === "guide" ? "active" : ""}`}
                  onClick={() => setActiveTab("guide")}
                >
                  Guide
                </button>
                <button
                  className={`tab ${activeTab === "try" ? "active" : ""}`}
                  onClick={() => setActiveTab("try")}
                >
                  Try It
                </button>
              </div>

              {/* Desktop Tab Content */}
              <div className="tab-content-desktop">
                {activeTab === "guide" && (
                  <>
                    <div className="content-section">
                      <h3 className="section-title">What to expect</h3>
                      <ul className="section-list">
                        <li>Candy-lit mushroom forests <b>{active.name}</b></li>
                        <li>Bold palettes</li>
                        <li>Cozy fantasy creatures</li>
                        <li>Soft glow and dreamlike lighting</li>
                      </ul>
                    </div>

                    <div className="content-section">
                      <h3 className="section-title">Prompt starters</h3>
                      <div className="prompts-list">
                        {starters.map((p, i) => (
                          <div key={i} className="prompt-item">
                            <code className="prompt-text">{p}</code>
                            <div className="prompt-actions">
                              <button 
                                className="btn-icon" 
                                onClick={() => copyPrompt(p)}
                                title="Copy prompt"
                              >
                                Copy
                              </button>
                              <button 
                                className="btn-try-mini" 
                                onClick={() => tryPrompt(p)}
                              >
                                Try →
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="content-section">
                      <h3 className="section-title">Use cases</h3>
                      <ul className="section-list">
                        <li>Story worlds & worldbuilding</li>
                        <li>Whimsical characters & scenes</li>
                        <li>Social visuals with bold, candy-glow vibes</li>
                      </ul>
                    </div>
                  </>
                )}

                {activeTab === "try" && (
                  <>
                    <h3 className="section-title">Quick start with {active.name}</h3>
                    <p className="section-hint">
                      Click any prompt to generate instantly:
                    </p>
                    
                    <div className="quick-prompts">
                      {starters.map((p, i) => (
                        <button
                          key={i}
                          className="quick-prompt-btn"
                          onClick={() => tryPrompt(p)}
                        >
                          <span className="prompt-icon">→</span>
                          <span className="prompt-label">{p}</span>
                          <span className="prompt-arrow">→</span>
                        </button>
                      ))}
                    </div>

                    <div className="or-divider">
                      <span>or write your own</span>
                    </div>

                    <Link 
                      className="btn btn-custom" 
                      to={{ pathname: "/create", search: trySearch("") }}
                    >
                      Custom Prompt
                    </Link>
                  </>
                )}

                {activeTab === "gallery" && (
                  <div className="gallery-info">
                    <p>
                      Viewing example {heroIx + 1} of {imgs.length}
                    </p>
                    <p className="hint-text">
                      Use ← → arrows to navigate
                    </p>
                  </div>
                )}
              </div>

              {/* CTA - Desktop */}
              <div className="floating-cta-desktop">
                <Link className="btn btn-primary" to={{ pathname: "/create", search: trySearch(starters[0]) }}>
                  Try {active.name}
                </Link>
                <Link className="btn" to="/creator">
                  Submit a Style 
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}
    </Screen>
  );
}

function StyleTag() {
  return (
    <style>{`
:root{
  --purple:#6D5EF9; --purpleDark:#5B4EE8; --purpleTint:#F3F0FF;
  --purpleBorder:#E7E2FF; --hairline:#E5E7EB; --shadow:0 14px 40px rgba(17,24,39,.12);
  --muted:#6B7280; --neutralText:#111827;
}

.styles-hero{text-align:center;margin-bottom:12px;}

/* Toolbar */
.styles-toolbar{display:flex;gap:10px;align-items:center;justify-content:center;flex-wrap:wrap;margin:8px auto 12px;}
.search{position:relative;}
.search input{height:42px;width:240px;padding:10px 34px 10px 12px;border-radius:12px;border:1px solid var(--hairline);outline:none;}
.search input:focus{border-color:var(--purple);box-shadow:0 0 0 3px var(--purpleTint);}
.search .clear{position:absolute;right:6px;top:50%;transform:translateY(-50%);width:28px;height:28px;border-radius:999px;border:1px solid var(--hairline);background:#fff;cursor:pointer;}

/* Dropdown styles */
.style-dropdown-wrapper{display:flex;align-items:center;gap:8px;}
.dropdown-label{font-weight:700;color:var(--neutralText);font-size:14px;}
.style-dropdown{
  height:42px;
  padding:0 36px 0 14px;
  border-radius:12px;
  border:1px solid var(--hairline);
  background:#fff url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8"><path fill="%23374151" d="M1.41 0L6 4.59 10.59 0 12 1.41l-6 6-6-6z"/></svg>') no-repeat right 12px center;
  font-weight:700;
  font-size:14px;
  cursor:pointer;
  appearance:none;
  min-width:180px;
}
.style-dropdown:focus{border-color:var(--purple);outline:none;box-shadow:0 0 0 3px var(--purpleTint);}

.no-results{color:var(--muted);font-weight:700;}

/* ===== MOBILE-FIRST LAYOUT ===== */
.floating-wrap{
  position:relative;
  padding:8px 4vw 80px;
  background:radial-gradient(800px 240px at 50% -20%, rgba(128,78,255,.10), transparent 55%);
}
.floating-card{
  width:min(1100px,100%);
  margin:0 auto;
  background:#fff;border:1px solid var(--hairline);
  border-radius:16px;box-shadow:var(--shadow);overflow:hidden;
}

/* Mobile: Stacked layout */
.floating-media-wrapper{display:flex;flex-direction:column;}
.floating-info{display:none;}

/* Style header */
.style-header-mobile{padding:12px 16px 10px;border-bottom:1px solid var(--hairline);}
.style-header-desktop{display:none;}

/* Style name row with badge */
.style-name-row{
  display:flex;
  align-items:center;
  gap:10px;
  flex-wrap:wrap;
  margin-bottom:4px;
}

.style-name{font-size:18px;font-weight:900;margin:0;line-height:1.2;}
.style-hint{margin:0;color:var(--muted);font-size:13px;line-height:1.4;}

/* Dev badge (yellow/gold) */
.dev-badge{
  display:inline-flex;
  align-items:center;
  padding:4px 10px;
  background:#FEF3C7;
  color:#92400E;
  font-size:11px;
  font-weight:800;
  border-radius:6px;
  border:1px solid #FCD34D;
  text-transform:uppercase;
  letter-spacing:0.05em;
  white-space:nowrap;
  cursor:default;
}

/* Tabs - Mobile */
.tabs-mobile{display:flex;border-bottom:1px solid var(--hairline);}
.tabs-desktop{display:none;}

.tab{
  flex:1;height:48px;border:none;background:transparent;
  font-weight:800;font-size:14px;color:var(--muted);
  cursor:pointer;position:relative;transition:all .2s;
}
.tab.active{color:var(--purple);}
.tab.active::after{
  content:'';position:absolute;bottom:0;left:0;right:0;
  height:3px;background:var(--purple);
}

/* Gallery */
.floating-media{position:relative;min-height:280px;background:#0b0b0e;}
.floating-media img{
  width:100%;height:100%;object-fit:cover;
  object-position:42% center;display:block;
}
.floating-fallback{position:absolute;inset:0;background:linear-gradient(180deg,#0b0b0e,#1a1530);}

.nav-arrow{
  position:absolute;top:50%;transform:translateY(-50%);
  width:36px;height:36px;border-radius:999px;
  border:1px solid var(--hairline);background:rgba(255,255,255,.9);
  color:#111827;display:grid;place-items:center;
  box-shadow:0 10px 22px rgba(17,24,39,.18);opacity:.9;
  transition:opacity .15s ease, transform .15s ease;cursor:pointer;
}
.nav-arrow:hover{opacity:1;transform:translateY(-50%) scale(1.04);}
.nav-arrow.left{left:8px;}
.nav-arrow.right{right:8px;}

.dots{position:absolute;left:12px;bottom:12px;display:flex;gap:8px;}
.dot{
  width:10px;height:10px;border-radius:999px;
  border:1px solid var(--hairline);background:#fff;
  opacity:.7;cursor:pointer;
}
.dot.on{background:var(--purple);border-color:var(--purple);opacity:1;}

/* Tab content - Mobile */
.tab-content-mobile{padding:14px 16px;}
.content-section{margin-bottom:16px;}
.section-title{font-weight:900;font-size:15px;margin:0 0 8px 0;}
.section-hint{margin:0 0 10px 0;color:var(--muted);font-size:13px;line-height:1.4;}
.section-list{margin:0;padding-left:18px;color:#151826;}
.section-list li{margin:4px 0;line-height:1.4;font-size:14px;}

/* Prompts */
.prompts-list{display:grid;gap:8px;}
.prompt-item{
  display:flex;align-items:center;gap:8px;
  padding:8px 10px;border:1px solid var(--hairline);
  border-radius:8px;background:#fafaff;
}
.prompt-text{
  flex:1;font-size:12px;line-height:1.3;
  display:block;word-break:break-word;
}
.prompt-actions{display:flex;gap:6px;flex-shrink:0;}
.btn-icon{
  padding:0 10px;height:34px;border-radius:8px;
  border:1px solid var(--hairline);background:#fff;
  font-size:12px;cursor:pointer;
  transition:all .2s;
  font-weight:700;
}
.btn-icon:hover{background:var(--purpleTint);border-color:var(--purple);}
.btn-try-mini{
  padding:0 10px;height:34px;border-radius:8px;
  border:1px solid var(--purple);background:var(--purple);
  color:#fff;font-weight:800;font-size:12px;
  cursor:pointer;transition:all .2s;white-space:nowrap;
}
.btn-try-mini:hover{background:var(--purpleDark);}

/* Quick prompts (Try tab) */
.quick-prompts{display:grid;gap:8px;margin-bottom:14px;}
.quick-prompt-btn{
  display:flex;align-items:center;gap:10px;
  padding:10px 12px;border:1px solid var(--hairline);
  border-radius:10px;background:#fff;
  cursor:pointer;transition:all .2s;text-align:left;
}
.quick-prompt-btn:hover{
  border-color:var(--purple);
  background:var(--purpleTint);
  transform:translateX(4px);
}
.prompt-icon{font-size:18px;flex-shrink:0;font-weight:900;color:var(--purple);}
.prompt-label{flex:1;font-weight:700;font-size:13px;line-height:1.3;}
.prompt-arrow{flex-shrink:0;color:var(--purple);font-weight:900;}

.or-divider{
  text-align:center;margin:14px 0;position:relative;
}
.or-divider::before{
  content:'';position:absolute;top:50%;left:0;right:0;
  height:1px;background:var(--hairline);
}
.or-divider span{
  position:relative;background:#fff;padding:0 10px;
  color:var(--muted);font-size:12px;font-weight:700;
}

/* Buttons */
.btn{
  height:42px;padding:0 16px;border-radius:10px;
  border:1px solid var(--hairline);background:#fff;
  font-weight:800;cursor:pointer;
  display:inline-flex;align-items:center;justify-content:center;
  text-decoration:none;transition:all .2s;
}
.btn-primary{background:var(--purple);color:#fff;border-color:var(--purple);}
.btn-primary:hover{background:var(--purpleDark);}
.btn-custom{width:100%;background:var(--purpleTint);color:var(--purple);border-color:var(--purpleBorder);}
.btn-custom:hover{background:var(--purple);color:#fff;}

/* Sticky CTA - Mobile Only */
.sticky-cta-mobile{
  position:fixed;bottom:0;left:0;right:0;
  padding:10px 12px;background:#fff;
  border-top:1px solid var(--hairline);
  box-shadow:0 -4px 12px rgba(0,0,0,.08);
  z-index:100;
}
.btn-sticky{width:100%;height:48px;font-size:15px;}

.floating-cta-desktop{display:none;}

/* Tab content desktop - initially hidden */
.tab-content-desktop{display:none;}

/* ===== TABLET: 680-1000px ===== */
@media (min-width:680px) and (max-width:999px){
  .floating-media{min-height:400px;}
  .style-name{font-size:24px;}
}

/* ===== MOBILE: < 640px ===== */
@media (max-width:640px){
  .styles-toolbar{
    flex-direction:column;
    width:100%;
    padding:0 4vw;
  }
  .search{width:100%;}
  .search input{width:100%;}
  .style-dropdown-wrapper{width:100%;justify-content:space-between;}
  .style-dropdown{flex:1;min-width:auto;}
  
  .floating-wrap{
    padding-left:max(16px, 4vw);
    padding-right:max(16px, 4vw);
  }
  
  /* Smaller badge on mobile */
  .dev-badge{
    font-size:10px;
    padding:3px 8px;
  }
  
  .style-name-row{
    gap:8px;
  }
}

/* ===== DESKTOP: 1000px+ ===== */
@media (min-width:1000px){
  /* Side-by-side layout */
  .floating-card{
    display:grid;
    grid-template-columns:1.3fr 1fr;
  }
  
  /* Show info panel */
  .floating-info{
    display:flex;
    flex-direction:column;
    padding:18px;
  }
  
  /* Hide mobile elements */
  .style-header-mobile{display:none;}
  .tabs-mobile{display:none;}
  .tab-content-mobile{display:none;}
  .sticky-cta-mobile{display:none;}
  
  /* Show desktop elements */
  .style-header-desktop{display:block;margin-bottom:16px;}
  .tabs-desktop{display:flex;border-bottom:1px solid var(--hairline);margin-bottom:16px;}
  
  /* Gallery visibility - hide when not on gallery tab */
  .floating-media{
    min-height:500px;
  }
  
  /* Show tab content on desktop */
  .tab-content-desktop{
    display:block;
    flex:1;
    overflow-y:auto;
  }
  
  .gallery-info{
    padding:20px;text-align:center;color:var(--muted);
  }
  .hint-text{font-size:13px;margin-top:8px;}
  
  /* CTA at bottom of right panel */
  .floating-cta-desktop{
    display:flex;gap:10px;margin-top:16px;
    padding-top:16px;border-top:1px solid var(--hairline);
  }
  
  /* Slightly larger badge on desktop */
  .dev-badge{
    font-size:12px;
    padding:5px 12px;
  }
}
    `}</style>
  );
}